import React, { useMemo, useState } from 'react';

import { Button, Text } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { usePermission } from '@admin/hooks/use_permission';
import { ACCESS_CONTROL_PERMISSIONS_ADMIN } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  AccessControlPermissionsAdminQuery,
  AccessControlRoleAdmin,
  AccessControlStaffUser,
  Status,
  useAccessControlUserRolesUpdateMutation,
} from '@admin/schema';

import {
  Actions,
  EmptyState,
  ExplorerPermissionItem,
  ExplorerPermissionList,
  ExplorerSidebar,
  Header,
  Layout,
  PermissionCard,
  PermissionDescription,
  PermissionTitle,
  RoleCheckboxGrid,
  ScrollableContent,
  SearchInput,
  SelectionMeta,
  SelectionTitle,
  Subtitle,
  Title,
  Toolbar,
  formatRoleName,
  formatRoleNames,
  matchesQuery,
} from './permissions_admin_shared';
import { roleIdsForUser } from './permissions_admin_utils';

type SelectedUser = Pick<AccessControlStaffUser, 'id' | 'name' | 'email' | 'roles' | 'explicitRoleIds'>;

type AccessControlPermissionsAdminUsersProps = {
  data: AccessControlPermissionsAdminQuery;
  onSave: () => void;
};

export const AccessControlPermissionsAdminUsers: React.FC<AccessControlPermissionsAdminUsersProps> = ({
  data,
  onSave,
}) => {
  const admin = data.accessControlPermissionsAdmin;
  const roles = admin.roles;
  const users = admin.staffUsers;

  const canUpdate = usePermission(
    ACCESS_CONTROL_PERMISSIONS_ADMIN.resource,
    ACCESS_CONTROL_PERMISSIONS_ADMIN.actions.update,
  );
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [execute, { loading }] = useAccessControlUserRolesUpdateMutation({ client });

  const filteredUsers = useMemo(
    () =>
      users.filter((user) =>
        matchesQuery(search, [user.name, user.email, user.roles.join(' '), formatRoleNames(user.roles)]),
      ),
    [users, search],
  );

  const onSelect = (user: SelectedUser) => {
    setSelectedUser(user);
    setSelectedRoleIds(roleIdsForUser(user, roles));
    setSaveError(null);
  };

  const toggleUserRole = (roleId: string) => {
    setSelectedRoleIds((current) =>
      current.includes(roleId) ? current.filter((id) => id !== roleId) : [...current, roleId],
    );
  };

  const saveUserRoles = async () => {
    if (!selectedUser) return;

    setSaveError(null);
    try {
      const response = await execute({
        variables: { userId: selectedUser.id, roleIds: selectedRoleIds },
      });
      const mutationResult = response.data?.accessControlUserRolesUpdate;

      if (mutationResult?.status !== Status.Ok) {
        setSaveError(mutationResult?.error ?? 'Failed to save user roles.');
        return;
      }
      if (!mutationResult.assignment) return;

      const updatedUser: SelectedUser = {
        ...selectedUser,
        explicitRoleIds: mutationResult.assignment.explicitRoleIds,
      };
      setSelectedUser(updatedUser);
      onSave();
    } catch (mutationError) {
      setSaveError(mutationError instanceof Error ? mutationError.message : 'Failed to save user roles.');
    }
  };

  return (
    <>
      <Toolbar>
        <SearchInput
          aria-label="Search users by name, email, or job role"
          placeholder="Search users by name, email, or job role"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </Toolbar>

      <Layout>
        <ExplorerSidebar>
          {filteredUsers.length === 0 ? (
            <EmptyState>No users match your search.</EmptyState>
          ) : (
            <ExplorerPermissionList>
              {filteredUsers.map((user) => (
                <ExplorerPermissionItem
                  key={user.id}
                  type="button"
                  active={user.id === selectedUser?.id}
                  onClick={() => onSelect(user)}
                >
                  <SelectionTitle>{user.name}</SelectionTitle>
                  <SelectionMeta>
                    {user.email}
                    <br />
                    {formatRoleNames(user.roles) || 'No job roles'} · {user.grantedPermissionCount} permissions
                  </SelectionMeta>
                </ExplorerPermissionItem>
              ))}
            </ExplorerPermissionList>
          )}
        </ExplorerSidebar>

        <Panel>
          <Panel.Body>
            {selectedUser ? (
              <>
                <Header>
                  <Title style={{ fontSize: 22 }}>{selectedUser.name}</Title>
                  <Subtitle>
                    {selectedUser.email} · job roles: {formatRoleNames(selectedUser.roles) || 'none'}
                  </Subtitle>
                </Header>

                <Subtitle style={{ marginTop: 16 }}>User roles (kept in sync with job roles)</Subtitle>
                <ScrollableContent>
                  <RoleCheckboxGrid>
                    {roles.map((role) => (
                      <PermissionCard key={role.id}>
                        <input
                          type="checkbox"
                          checked={selectedRoleIds.includes(role.id)}
                          disabled={!canUpdate}
                          onChange={() => toggleUserRole(role.id)}
                        />
                        <span>
                          <PermissionTitle>{formatRoleName(role.name)}</PermissionTitle>
                          <PermissionDescription>
                            {role.permissionIds.length} permissions granted to this role
                          </PermissionDescription>
                        </span>
                      </PermissionCard>
                    ))}
                  </RoleCheckboxGrid>
                </ScrollableContent>

                {saveError && <Text style="danger">{saveError}</Text>}

                {canUpdate && (
                  <Actions>
                    <Button kind="primary" loading={loading} onClick={saveUserRoles}>
                      Save user roles
                    </Button>
                  </Actions>
                )}
              </>
            ) : (
              <EmptyState>Select a user to manage their roles.</EmptyState>
            )}
          </Panel.Body>
        </Panel>
      </Layout>
    </>
  );
};

// Backward-compatible export for any external references.
export const UserRolesPanel = AccessControlPermissionsAdminUsers;
